import { create } from 'zustand';

import type { ConnectivitySnapshot } from './types';

export const EMPTY_OFFLINE_ELIGIBILITY = {
  eligible: false,
  reason: 'unsupported-app',
  businessId: null,
  inventoryCount: 0,
} as const;

export const INITIAL_CONNECTIVITY_SNAPSHOT: ConnectivitySnapshot = {
  runtimeState: 'CHECKING',
  issue: 'none',
  message: null,
  checkStage: 'idle',
  deviceConnected: typeof navigator === 'undefined' ? true : navigator.onLine,
  connectionType: 'unknown',
  internetReachable: null,
  apiReachable: null,
  offlineEligibility: EMPTY_OFFLINE_ELIGIBILITY,
};

type ConnectivityStore = ConnectivitySnapshot & {
  update: (patch: Partial<ConnectivitySnapshot>) => void;
  reset: () => void;
};

function snapshotValueEqual(
  key: keyof ConnectivitySnapshot,
  current: ConnectivitySnapshot[keyof ConnectivitySnapshot],
  next: ConnectivitySnapshot[keyof ConnectivitySnapshot]
): boolean {
  if (key !== 'offlineEligibility') return Object.is(current, next);
  const left = current as ConnectivitySnapshot['offlineEligibility'];
  const right = next as ConnectivitySnapshot['offlineEligibility'];
  return (
    left.eligible === right.eligible &&
    left.reason === right.reason &&
    left.businessId === right.businessId &&
    left.inventoryCount === right.inventoryCount
  );
}

export const useConnectivityStore = create<ConnectivityStore>((set) => ({
  ...INITIAL_CONNECTIVITY_SNAPSHOT,
  update: (patch) =>
    set((state) => {
      const changed = Object.entries(patch).some(
        ([key, value]) =>
          !snapshotValueEqual(
            key as keyof ConnectivitySnapshot,
            state[key as keyof ConnectivitySnapshot],
            value as ConnectivitySnapshot[keyof ConnectivitySnapshot]
          )
      );
      return changed ? patch : state;
    }),
  reset: () => set(INITIAL_CONNECTIVITY_SNAPSHOT),
}));
